import { Typography } from '@mui/material';
import {
  FilterKeys,
  FilterSection,
  GetStudentsQueryFilters,
  STUDENTS_ARRANGE_BY_FILTER_KEYS,
  STUDENTS_QUERY_FILTER_KEYS,
  StudentsArrangeBy,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  AgeGroupExpandedSelect,
  ArrangeByDropdown,
  CustomFieldTagSelect,
  DateDropdown,
  FilterDropdown,
  FiltersContainer,
  FiltersSidebarMethods,
  FilterTags,
  GenderExpandedSelect,
  GenderTagSelect,
  NationalityExpandedSelect,
  NationalityTagSelect,
  PersonalFiltersDropdown,
  pickOnlyParamsFromFilterKeys,
  PropertyTypeExpandedSelect,
  PropertyTypeTagSelect,
  renderPropertyGroupTags,
} from '@schooly/components/filters';
import { PROPERTIES_TEXT_IDS, SchoolPropertyType, SchoolUserRole } from '@schooly/constants';
import { Spin, TagSelect, TagSelectProperty } from '@schooly/style';
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';
import { useIntl } from 'react-intl';

import { StudentsFiltersSidebar } from './StudentsFiltersSidebar';
import { useStudentsFilters } from './useStudentsFilters';

type StudentsFiltersProps = {
  arrangeBy: StudentsArrangeBy | null;
  onSetArrangeBy: (v: StudentsArrangeBy | null) => void;
  schoolId: string;
  filters: GetStudentsQueryFilters;
  defaultFilters: GetStudentsQueryFilters;
  onSetFilters: (v: GetStudentsQueryFilters) => void;
  defaultUserFilters: GetStudentsQueryFilters;
  defaultUserArrangeBy: StudentsArrangeBy | null;
};

export type StudentsFilters = {
  openArrangeBy: () => void;
};

const StudentsFiltersComponent = (
  {
    arrangeBy,
    defaultFilters,
    filters,
    schoolId,
    onSetFilters,
    onSetArrangeBy,
    defaultUserFilters,
    defaultUserArrangeBy,
  }: StudentsFiltersProps,
  ref: React.ForwardedRef<StudentsFilters>,
) => {
  const sidebar = useRef<FiltersSidebarMethods | null>(null);
  const personalFiltersDropdown = useRef<PersonalFiltersDropdown | null>(null);
  const { currentStaff } = useAuth();

  const arrangeByDropdown = useRef<ArrangeByDropdown | null>(null);
  const { formatMessage } = useIntl();
  const {
    labels,
    arrangeByDropdownOptions,
    selectedItemsForAgeGroups,
    filtersDate,
    filtersAgeGroup,
    noStatusFilterSelected,
    noStatusOption,
    filtersStatus,
    filtersHouse,
    filtersMedicalNeeds,
    filtersCustomFields,
    filtersGender,
    filtersNationality,
    onSetHouseId,
    onClearHouse,
    onSetGender,
    onClearGender,
    onSetNationality,
    onClearNationality,
    onSetStatusId,
    onClearStatuses,
    onSetNoStatus,
    getSchoolLevelById,
    getAgeGroupById,
    getAgeGroupsByLevelId,
    onSetDate,
    onSetAgeGroup,
    onClearAgeGroup,
  } = useStudentsFilters({ schoolId, filters, onSetFilters });

  useImperativeHandle(
    ref,
    () => ({
      openArrangeBy: () => {
        setTimeout(() => arrangeByDropdown.current?.open(), 300);
      },
    }),
    [],
  );

  const handleSaveFilter = useCallback(() => {
    personalFiltersDropdown.current?.saveFilter();
  }, []);

  const handleOpenSidebar = useCallback(() => {
    sidebar.current?.open();
  }, []);

  return (
    <FiltersContainer onOpenFilters={handleOpenSidebar}>
      <PersonalFiltersDropdown
        ref={personalFiltersDropdown}
        currentUser={currentStaff}
        relationId={currentStaff?.relation_id || ''}
        onSaveFilter={handleSaveFilter}
        schoolId={schoolId}
        section={FilterSection.Student}
        filters={{ ...filters, arrange_by: arrangeBy ?? undefined }}
        onSetFilters={(v) => {
          const appliedFilters = pickOnlyParamsFromFilterKeys(STUDENTS_QUERY_FILTER_KEYS, v);

          onSetFilters({
            ...appliedFilters,
            [FilterKeys.Date]: appliedFilters.date || defaultFilters.date,
          });

          if (!v.arrange_by) {
            onSetArrangeBy(null);
            return;
          }
          for (const key of STUDENTS_ARRANGE_BY_FILTER_KEYS) {
            key === v.arrange_by && onSetArrangeBy(key);
          }
        }}
      />
      {filtersDate && <DateDropdown date={filtersDate} label={labels.date} onSetDate={onSetDate} />}

      {!!(filtersStatus?.length || noStatusFilterSelected) && (
        <FilterDropdown
          onClear={onClearStatuses}
          label={labels.status}
          tags={(open) => {
            if (noStatusFilterSelected) {
              return [
                <TagSelectProperty
                  key={noStatusOption.id}
                  userRole={SchoolUserRole.Student}
                  property={noStatusOption}
                  defaultValue={<Spin />}
                  sx={{ maxWidth: 200 }}
                  id={noStatusOption.id}
                  onClick={open}
                />,
              ];
            }

            return (filtersStatus || [])?.map((id) => (
              <PropertyTypeTagSelect
                userRole={SchoolUserRole.Student}
                schoolId={schoolId}
                sx={{ maxWidth: 200 }}
                key={id}
                id={id}
                onClick={open}
              />
            ));
          }}
        >
          {(onClose) => {
            const commonProps = {
              propertyType: SchoolPropertyType.Status,
              userRole: SchoolUserRole.Student,
              schoolId,
              onClose,
              onClear: onClearStatuses,
            };

            if (!arrangeBy) {
              return (
                <PropertyTypeExpandedSelect
                  {...commonProps}
                  selectedIds={noStatusFilterSelected ? null : filtersStatus || []}
                  onApply={(v: string[] | null) => {
                    if (v === null) {
                      onSetNoStatus();
                    } else {
                      onSetStatusId(v);
                    }
                  }}
                  noneOptionLabel={formatMessage({ id: PROPERTIES_TEXT_IDS[FilterKeys.Expired] })}
                />
              );
            }

            return (
              <PropertyTypeExpandedSelect
                {...commonProps}
                selectedIds={filtersStatus || []}
                onApply={onSetStatusId}
              />
            );
          }}
        </FilterDropdown>
      )}
      {!!filtersAgeGroup?.length && (
        <FilterDropdown
          onClear={onClearAgeGroup}
          label={labels.age_group}
          tags={(open) =>
            renderPropertyGroupTags({
              selectedItems: selectedItemsForAgeGroups,
              onClick: open,
              getProperty: (i) =>
                i.isGroup
                  ? getSchoolLevelById(i.id)
                  : { ...getAgeGroupById(i.id), type: SchoolPropertyType.AgeGroup },
              getTooltip: (i) =>
                i.isGroup
                  ? getAgeGroupsByLevelId(i.id).map((ageGroup) => (
                      <Typography key={ageGroup.id}>{ageGroup.name}</Typography>
                    ))
                  : null,
              tagProps: {
                userRole: SchoolUserRole.Student,
                sx: { maxWidth: 200 },
              },
            })
          }
        >
          {(onClose) => (
            <AgeGroupExpandedSelect
              userRole={SchoolUserRole.Student}
              schoolId={schoolId}
              selectedIds={filtersAgeGroup}
              onApply={onSetAgeGroup}
              onClose={onClose}
              onClear={onClearAgeGroup}
            />
          )}
        </FilterDropdown>
      )}
      {!!filtersHouse?.length && (
        <FilterDropdown
          onClear={onClearHouse}
          label={labels.house}
          tags={(open) =>
            filtersHouse.map((id) => (
              <PropertyTypeTagSelect
                userRole={SchoolUserRole.Student}
                sx={{ maxWidth: 200 }}
                schoolId={schoolId}
                key={id}
                id={id}
                onClick={open}
              />
            ))
          }
        >
          {(onClose) => (
            <PropertyTypeExpandedSelect
              propertyType={SchoolPropertyType.House}
              userRole={SchoolUserRole.Student}
              schoolId={schoolId}
              selectedIds={filtersHouse}
              onApply={onSetHouseId}
              onClose={onClose}
              onClear={onClearHouse}
            />
          )}
        </FilterDropdown>
      )}
      {!!filtersNationality?.length && (
        <FilterDropdown
          onClear={onClearNationality}
          label={labels.nationality}
          tags={(open) =>
            filtersNationality.map((v) => (
              <NationalityTagSelect sx={{ maxWidth: 200 }} key={v} nationality={v} onClick={open} />
            ))
          }
        >
          {(onClose) => (
            <NationalityExpandedSelect
              selectedNationalities={filtersNationality}
              onApply={onSetNationality}
              onClose={onClose}
              onClear={onClearNationality}
            />
          )}
        </FilterDropdown>
      )}
      {!!filtersGender?.length && (
        <FilterDropdown
          onClear={onClearGender}
          label={labels.gender}
          tags={(open) =>
            filtersGender.map((v) => (
              <GenderTagSelect sx={{ maxWidth: 200 }} key={v} gender={v} onClick={open} />
            ))
          }
        >
          {(onClose) => (
            <GenderExpandedSelect
              selectedGenders={filtersGender}
              onApply={onSetGender}
              onClose={onClose}
              onClear={onClearGender}
            />
          )}
        </FilterDropdown>
      )}
      {filtersMedicalNeeds && (
        <FilterTags
          label={labels.medical_needs}
          onClick={handleOpenSidebar}
          tags={() => [
            // biome-ignore lint/correctness/useJsxKeyInIterable: Legacy code
            <TagSelect
              label={formatMessage({ id: 'peopleDetail-AllergyMedicalConcerns' })}
              onClick={handleOpenSidebar}
            />,
          ]}
        />
      )}
      {!!filtersCustomFields?.length && (
        <FilterTags
          label={formatMessage({ id: 'peopleDetail-AdditionalInformation' })}
          onClick={handleOpenSidebar}
          tags={() =>
            filtersCustomFields.map((v) => (
              <CustomFieldTagSelect
                key={v.type}
                id={v.id}
                schoolId={schoolId}
                onClick={handleOpenSidebar}
              />
            ))
          }
        />
      )}
      {arrangeBy && (
        <ArrangeByDropdown
          ref={(r: any) => (arrangeByDropdown.current = r as ArrangeByDropdown)}
          onSelectOption={onSetArrangeBy}
          selectedOption={arrangeBy}
          options={arrangeByDropdownOptions}
          onClear={() => onSetArrangeBy(null)}
          onRemove={() => onSetArrangeBy(null)}
        />
      )}
      <StudentsFiltersSidebar
        ref={sidebar}
        defaultFilters={defaultFilters}
        filters={filters}
        onApply={(v) => {
          onSetFilters(v.filters);
          onSetArrangeBy(v.arrangeBy);
        }}
        arrangeBy={arrangeBy}
        schoolId={schoolId}
      />
    </FiltersContainer>
  );
};

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const StudentsFilters = forwardRef(StudentsFiltersComponent);
